import { useSetOverlayAtom } from "@/shared/atoms/useOverlayAtom";
import usePriorityModal from "@/shared/atoms/usePriorityModal";
import useOnClickOutside from "@/shared/hooks/useOnClickOutside";
import { cn } from "@/shared/utils/cn";
import React, { useEffect, useId, useMemo, useRef } from "react";
import useKeyPress from "../../shared/hooks/useKeyPress";
import IconButton from "../IconButton/IconButton";

export interface ModalProps {
  show?: boolean;
  onExit?: () => void;
  children?: React.ReactNode;
  width?: any;
  maxHeight?: any;
  minHeight?: any;
  fullHeight?: boolean;
  zIndex?: number;
  closeOnClickOutside?: boolean;
  className?: string;
  contentClassName?: string;
}

const Modal = ({
  width = "70rem",
  maxHeight = "95%",
  minHeight,
  fullHeight = false,
  show = true,
  onExit,
  children,
  zIndex,
  closeOnClickOutside = true,
  className,
  contentClassName,
}: ModalProps) => {
  const ref = useRef();
  const escClick = useKeyPress("Escape");
  const [{ priorityModal }] = usePriorityModal();
  const setOverlay = useSetOverlayAtom();
  const isShownPriorityModal = Boolean(priorityModal);

  const canClose = closeOnClickOutside && isShownPriorityModal === false;
  useOnClickOutside(ref, canClose ? onExit : undefined);

  const overlayId = useId();

  useEffect(() => {
    if (!show) return;
    setOverlay((draft) => {
      draft.modals = draft.modals.filter((m) => m.id !== overlayId);
      draft.modals.push({ id: overlayId, closeOnClickOutside });
    });
    return () => {
      setOverlay((draft) => {
        draft.modals = draft.modals.filter((m) => m.id !== overlayId);
      });
    };
  }, [show, setOverlay, closeOnClickOutside, overlayId]);

  useEffect(() => {
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  useEffect(() => {
    if (escClick && onExit && canClose) {
      onExit();
    }
  }, [escClick, onExit, canClose]);

  useEffect(() => {
    document.body.style.overflowY = show ? "hidden" : "auto";
  }, [show]);

  return useMemo(
    () => (
      <div
        ref={ref}
        className={cn(
          "overflow-y-auto w-[90%] md:w-260 lg:w-340 bg-white fixed top-1/2 left-1/2 rounded-md -translate-x-1/2 -translate-y-1/2 shadow-lg",
          show ? "block" : "hidden",
          isShownPriorityModal ? "w-[90%] md:w-240 lg:w-290" : "",
          className
        )}
        style={{
          ...({
            height: (fullHeight && { _: "95%", md: "unset" }) || undefined,
            maxHeight: maxHeight,
            minHeight: minHeight,
            zIndex: zIndex || 1001,
          } as any),
        }}
      >
        {onExit && (
          <div
            onClick={onExit || undefined}
            className="right-[2rem] top-6 z-10 absolute cursor-pointer"
          >
            <IconButton size="big" variants="times" />
          </div>
        )}
        <div className={cn("p-8 pt-10 md:pt-14 lg:pt-16", contentClassName)}>{children}</div>
      </div>
    ),
    [children, fullHeight, onExit, show, width, className, contentClassName]
  );
};

export default Modal;
